/**
 * TrialBanner — thin countdown strip shown to subscribers on trial.
 *
 * States:
 *   1. Trial active + seed credits remaining → "5 days left · 87 free credits"
 *   2. Trial active + seed credits ≤ 20      → "5 days left · 12 free credits · Top up"
 *   3. Trial active + seed credits = 0       → "Free credits used · Top up now"
 *   4. Trial expired + no top-ups yet         → "Trial ended · Top up to keep going"
 *   5. Non-trial user with remaining credits → hidden
 *
 * The banner is dismissible per session but never permanently — the countdown
 * matters. Uses `/api/trial/status` as the single source of truth.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Coins, X, Rocket } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function humanRemaining(seconds) {
  if (!seconds || seconds <= 0) return '0';
  const days = Math.floor(seconds / 86400);
  if (days >= 1) return `${days} day${days === 1 ? '' : 's'} left`;
  const hours = Math.floor(seconds / 3600);
  if (hours >= 1) return `${hours} hour${hours === 1 ? '' : 's'} left`;
  const mins = Math.max(1, Math.floor(seconds / 60));
  return `${mins} min${mins === 1 ? '' : 's'} left`;
}

export default function TrialBanner() {
  const [state, setState] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/trial/status`);
        if (!cancelled) setState(data);
      } catch (_e) {
        // Endpoint requires auth; ignore silently on public / logout.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!state || dismissed) return null;
  if (!state.is_trial && !state.needs_topup) return null;

  const seedRemaining = state.seed_credits?.remaining ?? 0;
  const seedIsTrialPool = state.seed_credits?.is_trial_pool;
  const trialExpired = !state.is_trial && seedIsTrialPool;
  const noCreditsLeft = seedIsTrialPool && seedRemaining <= 0;

  // Compose message + tone
  let tone = 'trial';
  let title = 'Trial active';
  let detail = '';
  if (trialExpired) {
    tone = 'expired';
    title = 'Trial ended';
    detail = 'Top up to keep every feature unlocked.';
  } else if (noCreditsLeft) {
    tone = 'warn';
    title = 'Free credits used up';
    detail = `${humanRemaining(state.seconds_remaining)} on your trial — top up to keep generating.`;
  } else if (seedIsTrialPool && seedRemaining <= 20) {
    tone = 'warn';
    title = `${humanRemaining(state.seconds_remaining)}`;
    detail = `Only ${seedRemaining} free credits left — top up when you're ready.`;
  } else if (state.is_trial) {
    tone = 'trial';
    title = humanRemaining(state.seconds_remaining);
    detail = seedIsTrialPool ? `${seedRemaining} free credits included · all features unlocked` : 'All features unlocked during your trial.';
  }

  const bgClass = tone === 'expired'
    ? 'bg-gradient-to-r from-red-600 to-orange-500'
    : tone === 'warn'
      ? 'bg-gradient-to-r from-amber-500 to-orange-500'
      : 'bg-gradient-to-r from-emerald-600 to-cyan-600';

  return (
    <div
      data-testid="trial-banner"
      className={`${bgClass} text-white px-4 py-2.5 flex items-center justify-between gap-3 text-sm`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="hidden sm:flex items-center gap-2 text-white/90">
          {tone === 'trial' ? <Rocket className="h-4 w-4" /> : <Clock className="h-4 w-4" />}
        </div>
        <div className="min-w-0">
          <span className="font-medium">{title}</span>
          <span className="text-white/85 ml-2 truncate">{detail}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Link
          to="/settings/credits"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 border border-white/20 transition text-white text-xs font-medium"
          data-testid="trial-banner-topup"
        >
          <Coins className="h-3.5 w-3.5" />
          {tone === 'expired' ? 'Top up now' : 'Top up'}
        </Link>
        <button
          onClick={() => setDismissed(true)}
          className="p-1.5 rounded-full hover:bg-white/10 transition"
          aria-label="Dismiss"
          data-testid="trial-banner-dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
